import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, Checkbox, FormGroup, FormControlLabel, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
    IAC_COURSE_OPTIONS, IAC_CORTICATION_OPTIONS,
    IAC_COMPRESSION_OPTIONS, IAC_DISPLACEMENT_OPTIONS,
    IANC_BIFURCATION_OPTIONS, IANC_BONY_BARRIER_OPTIONS,
    ROOT_NAME_OPTIONS
} from './ImpactionConstants';
import ImpactionTextPreview from './ImpactionTextPreview';
import MmInput from './MmInput';
import { generateIANCText } from '../utils/impactionTextGenerator';

// Helper for consistent label styling
const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
        {children}
    </Typography>
);

const ImpactionTab_IAC = ({ selectedTooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[selectedTooth] || {};
    const iacData = toothData.iac || {};
    const rootRelations = iacData.rootRelations || [];
    const crownRelations = iacData.crownRelations || [];

    const updateData = (field, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [selectedTooth]: {
                ...toothData,
                iac: {
                    ...iacData,
                    [field]: value
                }
            }
        });
    };

    // --- Dynamic Root Relation Handlers ---
    const addRootRelation = (rootName) => {
        const newList = [...rootRelations, {
            name: rootName,
            course: '',
            cortication: '',
            bonyBarrier: '',
            compression: '',
            displacement: '',
            proximityCervical: '',
            proximityMid: '',
            proximityApical: '',
            proximityApex: '',
            image: null
        }];
        updateData('rootRelations', newList);
        setAddRootDialogOpen(false);
    };

    const updateRootRelation = (index, field, value) => {
        const newList = [...rootRelations];
        newList[index] = { ...newList[index], [field]: value };
        updateData('rootRelations', newList);
    };

    const removeRootRelation = (index) => {
        const newList = rootRelations.filter((_, i) => i !== index);
        updateData('rootRelations', newList);
    };

    // --- Dynamic Crown Relation Handlers ---
    const addCrownRelation = () => {
        const newList = [...crownRelations, {
            occlusal: '',
            mid: '',
            cervical: '',
            image: null
        }];
        updateData('crownRelations', newList);
    };

    const updateCrownRelation = (index, field, value) => {
        const newList = [...crownRelations];
        newList[index] = { ...newList[index], [field]: value };
        updateData('crownRelations', newList);
    };

    const removeCrownRelation = (index) => {
        const newList = crownRelations.filter((_, i) => i !== index);
        updateData('crownRelations', newList);
    };

    const [addRootDialogOpen, setAddRootDialogOpen] = useState(false);
    const [selectedRootName, setSelectedRootName] = useState(ROOT_NAME_OPTIONS[0]);

    // Image Upload Helpers
    const handleRootImageUpload = async (index, type) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                if (type === 'root') updateRootRelation(index, 'image', fileData);
                else updateCrownRelation(index, 'image', fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveRootImage = (index, type) => {
        if (type === 'root') updateRootRelation(index, 'image', null);
        else updateCrownRelation(index, 'image', null);
    };

    const renderImageSection = (label, imageValue, onUpload, onRemove, height = '250px') => {
        const preview = imageValue ? imageValue.base64 : null;
        return (
            <Box sx={{ width: '250px', flexShrink: 0 }}>
                <Box sx={{ position: 'relative', mb: '10px' }}>
                    <Box
                        sx={{
                            width: '250px',
                            height: height,
                            border: '1px solid black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {preview && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={onRemove}
                                sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', zIndex: 1 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                        {preview ? (
                            <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                        )}
                    </Box>
                </Box>
                <Button variant={preview ? "outlined" : "contained"} fullWidth size="small" onClick={onUpload}>
                    {preview ? 'Replace' : 'Select'}
                </Button>
            </Box>
        );
    };

    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };
    const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', mb: '15px', pb: '5px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between' };
    const inputStyle = { '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiOutlinedInput-input': { padding: '8px 14px', fontSize: '14px' } };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary">
                Inferior Alveolar Canal Relationship ({selectedTooth})
            </Typography>

            {/* BIFURCATION (Optional) */}
            <Box sx={{ mb: 3 }}>
                <FieldLabel>Bifurcation / Trifurcation of Canal</FieldLabel>
                <Select
                    value={iacData.bifurcation || ''}
                    onChange={(e) => updateData('bifurcation', e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ ...inputStyle, minWidth: '200px' }}
                >
                    <MenuItem value=""><em>None Selected</em></MenuItem>
                    {IANC_BIFURCATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
            </Box>

            {/* CROWN RELATIONS */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>CROWN RELATION TO IANC</Typography>
            {crownRelations.map((item, index) => (
                <Box key={index} sx={sectionBoxStyle}>
                    <Box sx={sectionHeaderStyle}>
                        <Typography>Crown Relation #{index + 1}</Typography>
                        <IconButton size="small" color="error" onClick={() => removeCrownRelation(index)}><DeleteIcon /></IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row' }}>
                        {renderImageSection('Crown Relation Image', item.image, () => handleRootImageUpload(index, 'crown'), () => handleRemoveRootImage(index, 'crown'))}
                        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <Box>
                                <FieldLabel>Occlusal 3rd - Inferiorly</FieldLabel>
                                <MmInput width="100%" value={item.occlusal} onChange={(e) => updateCrownRelation(index, 'occlusal', e.target.value)} />
                            </Box>
                            <Box>
                                <FieldLabel>Mid 3rd - Inferiorly</FieldLabel>
                                <MmInput width="100%" value={item.mid} onChange={(e) => updateCrownRelation(index, 'mid', e.target.value)} />
                            </Box>
                            <Box>
                                <FieldLabel>Cervical 3rd - Inferiorly</FieldLabel>
                                <MmInput width="100%" value={item.cervical} onChange={(e) => updateCrownRelation(index, 'cervical', e.target.value)} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ))}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addCrownRelation} sx={{ mb: 4 }}>Add Crown Relation</Button>

            {/* ROOT RELATIONS */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>ROOT RELATION TO IANC</Typography>
            {rootRelations.map((item, index) => (
                <Box key={index} sx={sectionBoxStyle}>
                    <Box sx={sectionHeaderStyle}>
                        <Typography>{item.name} Relation</Typography>
                        <IconButton size="small" color="error" onClick={() => removeRootRelation(index)}><DeleteIcon /></IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
                        {renderImageSection(`${item.name} Image`, item.image, () => handleRootImageUpload(index, 'root'), () => handleRemoveRootImage(index, 'root'))}
                        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <FieldLabel>Course</FieldLabel>
                                <Select fullWidth size="small" sx={inputStyle} value={item.course} onChange={(e) => updateRootRelation(index, 'course', e.target.value)}>
                                    {IAC_COURSE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box>
                                <FieldLabel>Cortication</FieldLabel>
                                <Select fullWidth size="small" sx={inputStyle} value={item.cortication} onChange={(e) => updateRootRelation(index, 'cortication', e.target.value)}>
                                    {IAC_CORTICATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>

                            {/* Proximity Fields */}
                            <Box sx={{ gridColumn: 'span 2' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, mt: 1 }}>Proximity to the root</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                    <Box><FieldLabel>Cervical 3rd</FieldLabel><MmInput width="100%" value={item.proximityCervical} onChange={(e) => updateRootRelation(index, 'proximityCervical', e.target.value)} /></Box>
                                    <Box><FieldLabel>Mid 3rd</FieldLabel><MmInput width="100%" value={item.proximityMid} onChange={(e) => updateRootRelation(index, 'proximityMid', e.target.value)} /></Box>
                                    <Box><FieldLabel>Apical 3rd</FieldLabel><MmInput width="100%" value={item.proximityApical} onChange={(e) => updateRootRelation(index, 'proximityApical', e.target.value)} /></Box>
                                    <Box><FieldLabel>Root Apex</FieldLabel><MmInput width="100%" value={item.proximityApex} onChange={(e) => updateRootRelation(index, 'proximityApex', e.target.value)} /></Box>
                                </Box>
                            </Box>

                            <Box>
                                <FieldLabel>Bony Barrier Thickness</FieldLabel>
                                <Select fullWidth size="small" sx={inputStyle} value={item.bonyBarrier} onChange={(e) => updateRootRelation(index, 'bonyBarrier', e.target.value)}>
                                    {IANC_BONY_BARRIER_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>

                            <Box>
                                <FieldLabel>Compression</FieldLabel>
                                <Select fullWidth size="small" sx={inputStyle} value={item.compression} onChange={(e) => updateRootRelation(index, 'compression', e.target.value)}>
                                    {IAC_COMPRESSION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box>
                                <FieldLabel>Displacement</FieldLabel>
                                <Select fullWidth size="small" sx={inputStyle} value={item.displacement} onChange={(e) => updateRootRelation(index, 'displacement', e.target.value)}>
                                    {IAC_DISPLACEMENT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>

                        </Box>
                    </Box>
                </Box>
            ))}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddRootDialogOpen(true)} sx={{ mb: 4 }}>Add Root Relation</Button>

            <ImpactionTextPreview text={generateIANCText(iacData)} />

            {/* ADD ROOT DIALOG */}
            <Dialog open={addRootDialogOpen} onClose={() => setAddRootDialogOpen(false)}>
                <DialogTitle>Add New Root Relation</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, minWidth: '300px' }}>
                        <FieldLabel>Select Root Name</FieldLabel>
                        <Select
                            value={selectedRootName}
                            onChange={(e) => setSelectedRootName(e.target.value)}
                            fullWidth
                            size="small"
                        >
                            {ROOT_NAME_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRootDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => addRootRelation(selectedRootName)}>Add</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default ImpactionTab_IAC;
